import React, { useEffect, useMemo } from "react";
import { ArrowUpFromDot, BoxSelect, Brackets, Captions, ScanEye, Sparkles } from "lucide-react";
import { InspectorVisibilityEyeButton } from "../components/ui/InspectorVisibilityEyeButton";
import { InspectorIcon } from "../components/ui/Icons";
import { InspectorVisibilitySection } from "../components/property-panels/InspectorVisibilitySection";
import VisibilityIconRow from "../components/property-panels/VisibilityIconRow";
import { useBodyVisibilityControls } from "./useBodyVisibilityControls";
import { useBodies } from "../stores/sceneStore";
import Tooltip from "../components/ui/Tooltip";
import { useGraspStore } from "../stores/graspStore";
import { useViewerActions, useViewerStore } from "../stores/viewerStore";
import { resolveLibraryGraspFolderForRootBodyName } from "../utils/libraryGraspsSync";
import { LIBRARY_OBJECTS, libraryAssetHasGrasps } from "../utils/libraryCatalog";
import { useSelectionHighlightControl } from "../selected-highlight-visibility";
import {
  BODY_VISIBILITY_UI_EVENT,
  getBodyVisibilityUiState,
  setBodyVisibilityUiState,
} from "./visibilityEvents";

const BodyVisibilityToolbar = ({
  bodyUuid,
  className = "",
  sectionTitle = "Body Visibility",
  /** When true, show grasp-pose overlay toggle when this body subtree has grasp data (object properties). */
  includeGraspOverlay = false,
  /** When set, an inspect control is shown to the right of the overlay buttons. */
  onInspect,
  inspectTooltip = "Inspect body",
  highlightTargetType = "body",
  highlightTargetId = bodyUuid,
  showBodyLabelMarkerToggle = false,
}) => {
  const bodies = useBodies();
  const graspsByBodyUuid = useGraspStore((s) => s.graspsByBodyUuid);
  const loadGraspsForBody = useGraspStore((s) => s.actions.loadGraspsForBody);
  const bodyVisibility = useViewerStore((s) => s.bodyVisibility);
  const showGrasps = useViewerStore((s) => s.showGrasps);
  const toggleShowGrasps = useViewerStore((s) => s.actions.toggleShowGrasps);
  const viewerActions = useViewerActions();
  const thisBodyVisible = bodyUuid ? (bodyVisibility[bodyUuid] ?? true) : true;
  const [bodyVisibilityUi, setBodyVisibilityUi] = React.useState(() => getBodyVisibilityUiState());
  const { state, disabled, focusDisabled, toggleFocus, toggleBoundingBox, toggleOriginMarker } =
    useBodyVisibilityControls({ bodyUuid, enabled: true });
  const { highlight, toggleHighlight } = useSelectionHighlightControl({
    targetType: highlightTargetType,
    targetId: highlightTargetId,
    disabled,
    ensureVisible: () => {
      if (!useViewerStore.getState().showVisual) viewerActions.toggleShowVisual();
    },
  });

  const bodySubtreeUuids = useMemo(() => {
    if (!bodyUuid || !bodies?.[bodyUuid]) return [];
    const visited = new Set();
    const stack = [bodyUuid];
    while (stack.length > 0) {
      const uuid = stack.pop();
      if (!uuid || visited.has(uuid)) continue;
      visited.add(uuid);
      const body = bodies[uuid];
      if (!body) continue;
      (body.children || []).forEach((childUuid) => {
        if (childUuid && !visited.has(childUuid)) stack.push(childUuid);
      });
    }
    return Array.from(visited);
  }, [bodyUuid, bodies]);

  const childToParent = useMemo(() => {
    const map = {};
    if (!bodies) return map;
    for (const [uuid, body] of Object.entries(bodies)) {
      for (const childUuid of body.children || []) {
        if (childUuid) map[childUuid] = uuid;
      }
    }
    return map;
  }, [bodies]);

  /** Descendants of `bodyUuid` plus ancestors (grasps are stored on the object root body). */
  const graspScopeUuids = useMemo(() => {
    const set = new Set(bodySubtreeUuids);
    let p = bodyUuid;
    const visited = new Set();
    while (p && childToParent[p] != null && !visited.has(p)) {
      visited.add(p);
      const parent = childToParent[p];
      set.add(parent);
      p = parent;
    }
    return set;
  }, [bodySubtreeUuids, bodyUuid, childToParent]);

  const hasGraspsInSubtree = useMemo(() => {
    return [...graspScopeUuids].some((uuid) => (graspsByBodyUuid[uuid]?.length ?? 0) > 0);
  }, [graspScopeUuids, graspsByBodyUuid]);

  useEffect(() => {
    if (!includeGraspOverlay || !bodies) return;
    [...graspScopeUuids].forEach((uuid) => {
      if ((graspsByBodyUuid[uuid]?.length ?? 0) > 0) return;
      const folder = resolveLibraryGraspFolderForRootBodyName(bodies[uuid]?.name);
      const asset = LIBRARY_OBJECTS.find(
        (entry) => entry.folder === "objects" && entry.id === folder
      );
      if (libraryAssetHasGrasps(asset)) {
        void loadGraspsForBody(uuid, folder, { source: asset?.source });
      }
    });
  }, [includeGraspOverlay, bodies, graspScopeUuids, graspsByBodyUuid, loadGraspsForBody]);

  const showGraspControls = includeGraspOverlay && hasGraspsInSubtree;

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail || {};
      setBodyVisibilityUi({
        labels: !!detail.labels,
        objectLabels: !!detail.objectLabels,
        bodyLabelMarkers: !!detail.bodyLabelMarkers,
      });
    };
    window.addEventListener(BODY_VISIBILITY_UI_EVENT, handler);
    return () => window.removeEventListener(BODY_VISIBILITY_UI_EVENT, handler);
  }, []);

  const buttons = [
    {
      key: "highlight",
      title:
        highlightTargetType === "visualGeom"
          ? "Highlight selected visual geom"
          : "Highlight selected body",
      on: highlight,
      onClick: toggleHighlight,
      disabled,
      Icon: Sparkles,
    },
    {
      key: "focus",
      title: "Display selected only",
      on: state.focus,
      onClick: toggleFocus,
      disabled: focusDisabled,
      Icon: ScanEye,
    },
    {
      key: "bbox",
      title: "Bounding box (all geometry on this body)",
      on: state.boundingBox,
      onClick: toggleBoundingBox,
      disabled,
      Icon: BoxSelect,
    },
    {
      key: "origin",
      title: "Body origin marker (dot + positive-axis L + up arrow)",
      on: state.originMarker,
      onClick: toggleOriginMarker,
      disabled,
      Icon: ArrowUpFromDot,
    },
    ...(showBodyLabelMarkerToggle
      ? [
          {
            key: "bodyLabelMarkers",
            title: "Labels",
            on: !!bodyVisibilityUi.bodyLabelMarkers,
            onClick: () =>
              setBodyVisibilityUi(
                setBodyVisibilityUiState({
                  bodyLabelMarkers: !getBodyVisibilityUiState().bodyLabelMarkers,
                })
              ),
            disabled: false,
            Icon: Captions,
          },
        ]
      : []),
    ...(showGraspControls
      ? [
          {
            key: "grasps",
            title: "Grasp pose overlays (bracket markers, ranked candidates)",
            on: showGrasps,
            onClick: toggleShowGrasps,
            disabled: false,
            Icon: Brackets,
          },
        ]
      : []),
  ];

  return (
    <InspectorVisibilitySection
      sectionTitle={sectionTitle}
      className={className}
      headerActions={
        <div className="flex items-center gap-1">
          <InspectorVisibilityEyeButton
            visible={thisBodyVisible}
            onToggle={() => bodyUuid && viewerActions.setBodyVisibility(bodyUuid, !thisBodyVisible)}
            tooltipWhenOn="This body's visuals are visible — click to hide"
            tooltipWhenOff="This body's visuals are hidden — click to show"
            ariaLabel="Toggle this body's visual visibility"
          />
        </div>
      }
    >
      <VisibilityIconRow
        actions={buttons}
        trailingContent={
          typeof onInspect === "function" ? (
            <Tooltip content={inspectTooltip}>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  onInspect();
                }}
                className="menu-item ring-0 focus-visible:ring-0 focus-visible:outline-none shrink-0 h-8 w-8 rounded-sm border-0 p-0 flex items-center justify-center text-gray-200 transition-colors hover:bg-gray-700/90"
                style={{
                  backgroundColor: "transparent",
                }}
                aria-label={inspectTooltip}
              >
                <InspectorIcon className="h-4 w-4" />
              </button>
            </Tooltip>
          ) : null
        }
      />
      {showGraspControls && showGrasps ? (
        <div className="space-y-1 pt-0.5">
          <div
            className="h-2 w-full rounded"
            style={{
              background: "linear-gradient(to right, #c62828, #f9a825, #2e7d32)",
            }}
          />
          <div className="flex justify-between text-[10px] text-gray-400 select-none">
            <span>Side / upward</span>
            <span>Top-down</span>
          </div>
          <div className="text-[10px] panel-text-helper select-none">
            Showing top 10 grasps by current world top-down score.
          </div>
        </div>
      ) : null}
    </InspectorVisibilitySection>
  );
};

export default React.memo(BodyVisibilityToolbar);
